import type { Timestamp } from "firebase/firestore";
import { constructCurrency } from "common/currency";
import { useFirestore } from "@/hooks/firestore/core/useFirestore";
import { currentAutomationProgression } from "@/hooks/firestore/models/currentAutomationProgression";
import { useQuery } from "@tanstack/react-query";
import { useApiFetch } from "@/hooks/api/useApiFetch";
import { apiQueryKey } from "@/hooks/api/queryKey";
import { useUserId } from "@/features/auth/hooks/useUserId";
import { useFiatCurrency } from "@/hooks/firestore/user/useFiatCurrency";
import type { ProcessedBetEntry } from "./firestore/betting/useEntries";

type ProjectedRewardInfo = {
  max: number;
  percent: number;
  isPromo: boolean;
};

export type Action = {
  info: string;
  projectedRewardInfo: ProjectedRewardInfo;
  status: string;
  type: string;
};

type RewardInfo = {
  fiatCashRewarded: number;
  promoCashRewarded: number;
  projectedFiatCashReward: number;
  projectedPromoCashReward: number;
};

type Entity = {
  info: string;
  rewardInfo: RewardInfo;
  status: string;
  type: string;
  entityId: string;
};

export type Requirement = {
  info: string;
  projectedRewardInfo?: ProjectedRewardInfo;
  status: string;
  type: string;
};

type ProgressionCampaign = {
  name: string;
  description?: string;
  termsOfUse?: string;
  openToAllEligibleUsers: boolean;
  bettingTabs: string[];
  provider: string;
  status: string;
};

export type AutomationProgression = {
  actions: Action[];
  campaign: ProgressionCampaign;
  entities: Entity[];
  requirements: Requirement[];
  status: string;
  automationId: string;
  progressionId: string;
  name: string;
  expiresAt?: Timestamp;
};

export const useHistoricalUserProgression = (
  progressionId: string,
  shouldFetch: boolean,
): [AutomationProgression | undefined, boolean, any] => {
  const userId = useUserId();
  const apiFetch = useApiFetch();

  const { data, error } = useQuery({
    queryKey: apiQueryKey("automations/progressions", {
      userId,
      progressionId,
    }),
    queryFn: () =>
      apiFetch<AutomationProgression>(
        `${process.env.GATSBY_API_URI}/automations/progressions/${progressionId}`,
      ),
    enabled: shouldFetch,
    meta: { userScoped: true },
  });

  if (!shouldFetch) {
    return [undefined, false, null];
  }

  return [data, !error && !data, error];
};

export const useLiveUserProgression = (
  progressionId: string,
  shouldFetch: boolean,
): [AutomationProgression | undefined, boolean] => {
  const { data, isLoading } = useFirestore(
    shouldFetch && progressionId
      ? `users/{userId}/currentAutomationProgressions/${progressionId}`
      : undefined,
    currentAutomationProgression,
  );

  if (!data) {
    return [undefined, isLoading];
  }

  const progression: AutomationProgression = {
    automationId: data.automationId,
    progressionId: data.progressionId,
    name: data.name,
    status: data.status ?? "ACTIVE",
    campaign: {
      name: data.campaign.name,
      description: data.campaign.description ?? undefined,
      termsOfUse: data.campaign.termsOfUse ?? undefined,
      openToAllEligibleUsers: data.campaign.openToAllEligibleUsers,
      bettingTabs: data.campaign.bettingTabs,
      provider: data.campaign.provider,
      status: data.campaign.status,
    },
    expiresAt: data.expiresAt,
    actions: (data.actions ?? []).map((action) => ({
      info: action.info,
      status: action.status ?? "",
      type: action.type,
      projectedRewardInfo: {
        max: action.projectedRewardInfo?.max ?? 0,
        percent: action.projectedRewardInfo?.percent ?? 0,
        isPromo: action.projectedRewardInfo?.isPromo ?? false,
      },
    })),
    requirements: (data.requirements ?? []).map((req) => ({
      info: req.info,
      status: req.status ?? "",
      type: req.type,
      projectedRewardInfo: req.projectedRewardInfo
        ? {
            max: req.projectedRewardInfo.max ?? 0,
            percent: req.projectedRewardInfo.percent ?? 0,
            isPromo: req.projectedRewardInfo.isPromo ?? false,
          }
        : undefined,
    })),
    entities: data.entities.map((entity) => ({
      entityId: entity.entityId,
      info: entity.info,
      status: entity.status,
      type: entity.type,
      rewardInfo: {
        fiatCashRewarded: entity.rewardInfo?.fiatCashRewarded ?? 0,
        promoCashRewarded: entity.rewardInfo?.promoCashRewarded ?? 0,
        projectedFiatCashReward:
          entity.rewardInfo?.projectedFiatCashReward ?? 0,
        projectedPromoCashReward:
          entity.rewardInfo?.projectedPromoCashReward ?? 0,
      },
    })),
  };

  return [progression, isLoading];
};

export const useUserProgressionData = (
  progressionId: string,
  isSettled: boolean,
) => {
  // We should only fetch live progression if the entry is not settled
  const shouldFetchLive = !isSettled;
  const [progression, loading] = useLiveUserProgression(
    progressionId,
    shouldFetchLive,
  );

  // We should only fetch historical progression if the live progression is not available
  const shouldFetchHistorical = !loading && !progression;
  const [historicalProgression, loadingHistorical] =
    useHistoricalUserProgression(progressionId, shouldFetchHistorical);

  return {
    progression: progression || historicalProgression,
    loading: loading || loadingHistorical,
  };
};

export const useUserProgression = (
  progressionId: string,
  entry: ProcessedBetEntry,
): {
  campaign?: ProgressionCampaign;
  isComplete?: boolean;
  isFailed?: boolean;
  isPromo?: boolean;
  loading: boolean;
  displayPayout?: string;
  automationId?: string;
  progression?: AutomationProgression;
  hide?: boolean;
} => {
  const usersCurrency = useFiatCurrency();
  const isSettled = entry.data.status === "SETTLED";

  const { progression, loading } = useUserProgressionData(
    progressionId,
    isSettled,
  );

  if (loading || !progression) {
    return {
      loading,
      progression: undefined,
      hide: false,
    };
  }

  const { status, campaign, entities, automationId } = progression;

  const rewardInfo = (entities || []).find(
    (entity) => entity.entityId === entry.referenceId,
  )?.rewardInfo;

  if (!rewardInfo) {
    return {
      loading,
      progression,
      hide: true,
    };
  }

  const actualPayout =
    rewardInfo?.fiatCashRewarded > 0
      ? rewardInfo?.fiatCashRewarded
      : rewardInfo?.promoCashRewarded ?? 0;

  const isComplete =
    actualPayout > 0 || (status !== "ACTIVE" && status !== "FAILED_TO_ACTION");
  const isFailed = actualPayout === 0 && isComplete;
  const isPromo = rewardInfo.projectedPromoCashReward > 0;

  const payout = isComplete
    ? actualPayout
    : rewardInfo.projectedFiatCashReward > 0
      ? rewardInfo.projectedFiatCashReward
      : rewardInfo.projectedPromoCashReward;

  const displayPayout = `${constructCurrency(payout, {
    currencyType: usersCurrency,
  })} ${isPromo ? "Bonus Cash" : "Cash"}`;

  return {
    progression,
    loading,
    isFailed,
    displayPayout,
    isComplete,
    isPromo,
    campaign,
    automationId,
    hide: false,
  };
};
